import React, { memo, useMemo, useState } from 'react';

import { Box } from '@mui/material';
import { useQueries } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';

import httpInstances from '@/api/axiosInstance';
import SymbolsPreview, { SymbolPreviewItem } from '@/components/SymbolsPreview';
import Typography from '@/components/Typography';
import LoadingIndicator from '@/features/AIChat/AskGXDrawer/components/LoadingIndicator/LoadingIndicator';
import LoadingText from '@/features/AIChat/AskGXDrawer/components/LoadingText';
import {
  MarkdownH1,
  MarkdownH2,
  MarkdownH3,
  MarkdownLink
} from '@/features/AIChat/AskGXDrawer/Messages/MarkdownComponents/MarkdownComponents';
import RateAnswer from '@/features/AIChat/AskGXDrawer/Messages/RateAnswer/RateAnswer';
import {
  extractGxCodesFromMessage,
  GX_REGEX,
  gxQueryKey,
  toCatalogueItem
} from '@/features/AIChat/AskGXDrawer/utils/gxCodes';
import { useChat } from '@/features/AIChat/context/useChat';
import { HumanMessage, Message } from '@/features/AIChat/context/utils';
import WidgetMapper from '@/features/AIChat/widgets/WidgetMapper';
import { FeedLink } from '@/pages/Markets/MarketsComponents/Feed/components/FeedLink';

const { axiosDataInstance } = httpInstances;

type MessageComponentProps = {
  msg: Message;
  index: number;
};

const MessageComponent = ({ msg, index }: MessageComponentProps) => {
  const { isSending, conversationId, version, messages } = useChat();

  const isAI = msg.sender === 'ai';
  const isLastMessage = index === messages.length - 1;

  const [isOpen, setIsOpen] = useState(false);

  const gxCodes = useMemo(() => extractGxCodesFromMessage(msg), [msg]);

  const queries = useMemo(
    () =>
      gxCodes.map(code => ({
        queryKey: gxQueryKey(code),
        queryFn: async () => {
          const resp = await axiosDataInstance.get(`/catalogue?Code=${code}`, {
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              fields: 'Title,Code,Alias,Currency,Units,PeriodType,Period,TimeRef'
            }
          });
          return toCatalogueItem(resp.data[0]);
        },
        enabled: gxCodes.length > 0
      })),
    [gxCodes]
  );

  const results = useQueries({
    combine: rs => ({
      data: rs.map(r => r.data as SymbolPreviewItem).filter(r => !!r?.symbol.Code),
      pending: rs.some(r => r.isPending)
    }),
    queries
  });

  const markdownComponents = useMemo(
    () => ({
      a: MarkdownLink,
      h1: MarkdownH1,
      h2: MarkdownH2,
      h3: MarkdownH3
    }),
    []
  );

  return (
    <Box
      sx={{
        padding: isAI ? '0' : '12px',
        maxWidth: isAI ? '100%' : '70%',
        marginBottom: '12px',
        minWidth: isAI ? '100%' : '208px',
        alignSelf: isAI ? 'flex-start' : 'flex-end',
        backgroundColor: isAI
          ? 'inherit'
          : 'var(--GX-action-bar-button-tile, rgba(248, 248, 248, 0.20))'
      }}
    >
      <Typography
        component='div'
        sx={{
          overflowWrap: 'break-word',
          hyphens: 'auto',
          fontSize: '14px'
        }}
      >
        {isAI ? (
          <>
            {msg.content.map(part => {
              switch (part.kind) {
                case 'text':
                  return (
                    <ReactMarkdown
                      key={part.text}
                      components={markdownComponents}
                    >
                      {part.text.replace(GX_REGEX, match => `[${match}](/${match})`)}
                    </ReactMarkdown>
                  );
                case 'tool_result':
                  if (part.display === 'table') {
                    return (
                      <WidgetMapper
                        key={part.tool_call_id}
                        widgetData={part}
                      />
                    );
                  }
                  return null;
                default:
                  return null;
              }
            })}
            {(!isSending || !isLastMessage) && !!gxCodes.length && (
              <>
                <FeedLink
                  CTAType='Symbol'
                  CTAValue=''
                  open={isOpen}
                  setOpen={setIsOpen}
                />
                {isOpen && (
                  <SymbolsPreview
                    forChat
                    enableQuery={false}
                    items={results.data}
                  />
                )}
              </>
            )}
          </>
        ) : (
          <>
            <Box sx={{ fontWeight: 700 }}>You:</Box>
            <Box>{msg.text}</Box>
          </>
        )}
      </Typography>

      {isAI && (
        <Box
          sx={{
            mt: 1,
            display: isLastMessage && !isSending ? 'flex' : undefined,
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          {isLastMessage && isSending && <LoadingText firstChunkArrived={msg.content.length > 0} />}
          {isLastMessage && <LoadingIndicator animate={isSending} />}
          {((isLastMessage && !isSending) || !isLastMessage) && (
            <RateAnswer
              conversationId={conversationId || ''}
              prompt={(messages[index - 1] as HumanMessage).text}
              response={msg.content.map(part => {
                switch (part.kind) {
                  case 'text':
                    return part.text;
                  case 'tool_result':
                    if (part.display === 'table') {
                      return part.rows;
                    }
                    return;
                  default:
                    return;
                }
              })}
              version={version}
            />
          )}
        </Box>
      )}
    </Box>
  );
};
export default memo(MessageComponent);
